import React from 'react';

// Components
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';

// Types
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import { canAccessAIStatementReader, canAccessTaxAnalysis, canAccessUploadCSV } from '@/utils/feature-access';

// Assets
import EmptyPorfolioAIStatementReader from '@/images/design-system-v2/empty-state/emptyPortfolioAIStatementReader.png';

const i18nBasePath = 'PortfolioEditorBuilder.Content.HoldingsTable.EmptyData.NoHoldings';

type Props = {
  user?: Nullable<User>;
  comparisonItem?: ComparisonItem;
  onOpenAIStatementReader: () => void;
  onOpenUploadModal: () => void;
};

const NoHoldingsEmptyState = ({ user, comparisonItem, onOpenAIStatementReader, onOpenUploadModal }: Props): JSX.Element => {
  const canUploadCSV = canAccessUploadCSV(user);
  const canUseAIStatementReader = canAccessAIStatementReader(user);
  const hasTaxAnalysisPermission = canAccessTaxAnalysis(user, comparisonItem?.portfolioType);

  if (!canUploadCSV && !canUseAIStatementReader && !hasTaxAnalysisPermission) {
    return <EmptyStateCard titlePath={`${i18nBasePath}.Title`} messagePath={`${i18nBasePath}.Message`} />;
  }

  if (canUseAIStatementReader && hasTaxAnalysisPermission) {
    const i18nPath = `${i18nBasePath}.TaxAnalysisAndAIStatementReader`;
    const primaryButton = {
      label: `${i18nPath}.StatementButton`,
      onClick: onOpenAIStatementReader,
    };
    const secondaryActions = {
      links: [{ label: `${i18nPath}.TaxCsvButton`, onClick: onOpenUploadModal }],
    };

    return <EmptyStateCard titlePath={`${i18nPath}.Title`} messagePath={`${i18nPath}.Message`} primaryButton={primaryButton} {...secondaryActions} />;
  }

  if (canUseAIStatementReader) {
    const i18nPath = `${i18nBasePath}.AIStatementReader`;
    const secondaryActions = {
      links: [
        {
          icon: 'upload-new',
          label: `${i18nPath}.Button`,
          onClick: onOpenAIStatementReader,
        },
      ],
    };
    return (
      <EmptyStateCard imagePath={EmptyPorfolioAIStatementReader} titlePath={`${i18nPath}.Title`} messagePath={`${i18nPath}.Message`} {...secondaryActions} />
    );
  }

  if (hasTaxAnalysisPermission) {
    const i18nPath = `${i18nBasePath}.TaxAnalysis`;
    const secondaryActions = {
      links: [{ icon: 'upload-new', label: `${i18nPath}.TaxCsvButton`, onClick: onOpenUploadModal }],
    };

    return <EmptyStateCard titlePath={`${i18nPath}.Title`} messagePath={`${i18nPath}.Message`} {...secondaryActions} />;
  }

  const secondaryActions = {
    links: [{ icon: 'upload-new', label: `${i18nBasePath}.Button`, onClick: onOpenUploadModal }],
  };

  return <EmptyStateCard titlePath={`${i18nBasePath}.Title`} messagePath={`${i18nBasePath}.MessageUploadCSV`} {...secondaryActions} />;
};

export default NoHoldingsEmptyState;
